import './cancel-button';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
const { isEmpty, sprintf } = _uo.utility;

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logLoop.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-loop-status-in-progress' )
export class AutomatorLogItemLoopStatusInProgress extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// The post ID of the loop
	@property( { type: Number, attribute: 'loop-id' } ) accessor loopID;

	// A unique code created to detect this specific loop run in this specific recipe run
	// The backend will use this to cancel/pause/resume the loop
	@property( { type: String, attribute: 'loop-process-code' } ) accessor processCode;

	@property( { type: Number, attribute: 'elements-completed' } ) accessor elementsCompleted = 0;

	@property( { type: Number, attribute: 'elements-total' } ) accessor elementsTotal;

	// The date the loop started running
	@property( { type: String, attribute: 'start-date' } ) accessor startDate;

	// The difference between both start and end dates
	@property( { type: Object, attribute: false } ) accessor dateElapsed;

	// Information about when the next batch of this loop will run
	@property( { type: Object, attribute: false } ) accessor dateNextProcess;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="progress">
				<div class="progress-header">
					<div class="progress-header-summary">
						<div class="progress-info">
							${ this._templateStatus }
							${ this._templateNumberProcessed }
						</div>

						<div class="progress-details">
							${ this._templateDate }
							${ this._templateNextBatch }
						</div>
					</div>

					${ this._templateQuickActions }
				</div>
				
				${ this._templateProgressBar }
			</div>
		`;
	}

	/**
	 * Creates the status icon and text.
	 *
	 * @return {TemplateResult} The status
	 */
	get _templateStatus() {
		return html`
			<span class="progress-info-status">
				${ ! this.isImportMode ?
					html`
						<uo-icon id="loading" animation="spin"></uo-icon>
					` :
					html`
						<uap-log-status status-id="in-progress" hide-label></uap-log-status>
					`
				}
				${ strings.inProgress }
			</span>
		`;
	}

	/**
	 * Render the number of elements processed
	 *
	 * @return {TemplateResult} The number of elements processed
	 */
	get _templateNumberProcessed() {
		return html`
			<div class="progress-info-text">
				<span class="progress-info-completed">
					${ sprintf(
						strings.progressStatus,
						this.elementsCompleted.toLocaleString(),
						this.elementsTotal.toLocaleString()
					) }
				</span>
				<span class="progress-info-percentage">
					(${ this.percentage }%)
				</span>
			</div>
		`;
	}

	/**
	 * Returns the HTML of the section that shows the start and end date
	 *
	 * @return {TemplateResult} The date
	 */
	get _templateDate() {
		// If it's in progress, show the relative date, but add "Started"
		if (
			// Only show the timer if we're not in import mode
			! this.isImportMode &&
			! isEmpty( this.dateElapsed ) &&
			! isEmpty( this.dateElapsed.elapsed_in_seconds )
		) {
			return html`
				<uo-tooltip>
					${ this.startDate }

					<div slot="target">
						<uo-icon id="clock"></uo-icon>
						${ strings.started } 
						<uo-timer 
							seconds-left=${ ( this.dateElapsed.elapsed_in_seconds * -1 ) }
						></uo-timer>
					</div>
				</uo-tooltip>
			`;
		}

		// Otherwise, just show the start date
		return html`
			<div>
				<uo-icon id="clock"></uo-icon>
				${ this.startDate }
			</div>
		`;
	}

	/**
	 * Renders the quick actions of the loop when it's in progress
	 *
	 * @return {TemplateResult} The quick actions
	 */
	get _templateQuickActions() {
		return html`
			<div class="progress-header-quick-actions">
				${ this._templateQuickActionCancel }
			</div>
		`;
	}

	/**
	 * Renders the quick actions of the loop when it's in progress
	 *
	 * @return {TemplateResult} The quick actions
	 */
	get _templateQuickActionCancel() {
		return html`
			<uap-log-loop-status-in-progress-cancel
				loop-id=${ this.loopID }
				loop-process-code=${ this.processCode }
				?import-mode=${ this.isImportMode }
			></uap-log-loop-status-in-progress-cancel>
		`;
	}

	/**
	 * Renders when will be the next batch of the loop
	 *
	 * @return {TemplateResult} The date
	 */
	get _templateNextBatch() {
		// Show it only if the loop is in progress and there is data
		if (
			// Don't add this if it's import mode, independently of the other properties of this component
			this.isImportMode ||
			isEmpty( this.dateNextProcess ) ||
			isEmpty( this.dateNextProcess.delay_in_seconds ) ||
			isEmpty( this.dateNextProcess.formatted )
		) {
			return;
		}

		return html`
			<uo-tooltip>
				${ this.dateNextProcess.formatted }

				<div slot="target">
					<uo-icon id="repeat"></uo-icon>
					${ strings.nextBatch } 
					<uo-timer 
						seconds-left=${ this.dateNextProcess.delay_in_seconds }
						@uo-timer-finished=${ () => {
							// Request the log to update its object
							this.dispatchEvent(
								new CustomEvent(
									'uap-log-request-data-refresh',
									{
										bubbles: true,
										composed: true
									}
								)
							);
						} }
					></uo-timer>
				</div>
			</uo-tooltip>
		`;
	}

	/**
	 * Creates the progress bar
	 *
	 * @return {TemplateResult} The progress bar
	 */
	get _templateProgressBar() {
		return html`
			<div class="progress-bar">
				<div 
					class="progress-bar-indicator"
					style="width: ${ this.percentage }%"
				></div>
			</div>
		`;
	}

	/**
	 * Calculates the percentage using the completed and total elements
	 *
	 * @return {Number} The percentage
	 */
	get percentage() {
		return Math.round( 100 * this.elementsCompleted / this.elementsTotal );
	}
}